import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { Company } from '../../models/company.model';
import { GlobalSwal } from '../../styles/swal.style';
import { StorageOperationDA } from '../../services/admin/storage.operation.service';
import { ProviderOrderDA } from '../../services/admin/provider.order.service';
import { Error } from '../../models/error.model';
import { ProviderOrder } from '../../models/provider.order.model';
import { StorageCenter } from '../../models/storage.center.model';
import { modalHide } from '../../utils/modal.util';
import { legendInvalid, legendValid, legendValidInvalidRestart } from '../../utils/legend.data.util';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';
import { storageCenterSelected } from '../../scripts/list.selected.script';

import AppLoading from '../Loading';
import AppLegend from '../element/Legend';

export interface AppModalProviderOrderStorageCenterProps {
  companyForUser: Company | null,
  storageCentersForCompany: StorageCenter[] | null,
  providerOrder: ProviderOrder | null,
  setProviderOrder: Dispatch<SetStateAction<ProviderOrder | null>>
};

interface InventoryLine {
  id_variant: string;
  detail: string;
  quantity_ordered: number;
  quantity_received: number;
  quantity_remaining: number;
  quantity_to_enter: number;
}

let errorResponse: Error, providerOrderResponse: ProviderOrder;

const AppModalProviderOrderStorageCenter: React.FunctionComponent<AppModalProviderOrderStorageCenterProps> = ({companyForUser, storageCentersForCompany, providerOrder, setProviderOrder}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [loadingOperations, setLoadingOperations] = useState(false)
  const [storageCenter, setStorageCenter] = useState({value: '', valid: false})
  const [lines, setLines] = useState<InventoryLine[]>([])

  const allReceived = lines.length > 0 && lines.every((line) => line.quantity_remaining <= 0)
  const hasLinesToEnter = lines.some((line) => line.quantity_to_enter > 0)

  const handleChangeStorageCenter = (item: string) => {
    setStorageCenter({value: item, valid: true})
    legendValid('modal-provider-order-storage-center-legend-storage-center-required')
  }

  const handleChangeQuantity = (index: number, value: string) => {
    const numValue = Math.max(0, Math.min(Number(value) || 0, lines[index].quantity_remaining))
    setLines(lines.map((line, i) => i === index ? {...line, quantity_to_enter: numValue} : line))
  }

  const submitProviderOrderValidate = async () => {
    setLoadIndicator('on')

    if (!storageCenter.valid) {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then(() => {
        if (!storageCenter.value.length) legendInvalid('modal-provider-order-storage-center-legend-storage-center-required')
        setLoadIndicator('off')
      })
      return
    }

    if (!hasLinesToEnter) {
      GlobalSwal.fire({
        text: lang.labels.selectAtLeastOneQuantity,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then(() => {
        setLoadIndicator('off')
      })
      return
    }

    if (companyForUser && providerOrder) {
      let source = {id: providerOrder.id, type: 'provider-order', number: providerOrder.provider_order_number ?? ''}
      let list = lines
        .filter((line) => line.quantity_to_enter > 0)
        .map((line) => ({id_variant: line.id_variant, quantity: line.quantity_to_enter}))

      await StorageOperationDA.postStorageOperations(companyForUser.id, 'in', source, null, storageCenter.value, list).then( async (response) => {
        if (response.status === 201) {
          const operationId = response.data?.id ?? response.data?._id

          // Mettre à jour ids_storage_operations sur la commande fournisseur
          if (operationId) {
            await ProviderOrderDA.patchProviderOrderStorageOperations(companyForUser.id, providerOrder.id, operationId)
          }

          // Recharger la commande fournisseur pour obtenir le stock_status mis à jour
          const reloadResponse = await ProviderOrderDA.getProviderOrder(companyForUser.id, providerOrder.id)
          if (reloadResponse.status === 200) {
            setProviderOrder(reloadResponse.data?.data ?? reloadResponse.data)
          }

          GlobalSwal.fire({
            title: lang.labels.successfullyCreatedStorageOperations,
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            executeHideModalOrderStorageCenter()
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    }
  }

  const executeHideModalOrderStorageCenter = () => {
    modalHide('modal-provider-order-storage-center')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    legendValidInvalidRestart('modal-provider-order-storage-center-legend-storage-center-required')
    setStorageCenter({value: '', valid: false})
    setLines([])
  }

  useEffect( () => {
    if (companyForUser && providerOrder) {
      const orderedLines: { id_variant: string; quantity: number; detail: string }[] = []
      for (const req of providerOrder.requirements) {
        if (req.type === 'product') {
          for (const content of req.contents) {
            if ((content.reference ?? '').length > 0) {
              orderedLines.push({
                id_variant: content.reference,
                quantity: content.quantity,
                detail: content.detail,
              })
            }
          }
        }
      }

      const idsOps = providerOrder.ids_storage_operations ?? []

      if (idsOps.length > 0) {
        setLoadingOperations(true)
        StorageOperationDA.getStorageOperationsByIds(companyForUser.id, idsOps).then((operations) => {
          const enteredQuantities: Record<string, number> = {}
          for (const op of operations) {
            for (const line of (op.lines ?? [])) {
              const variantId = line.variant?.id ?? (line as any).id_variant ?? ''
              if (variantId) {
                enteredQuantities[variantId] = (enteredQuantities[variantId] || 0) + line.quantity
              }
            }
          }

          const inventoryLines: InventoryLine[] = orderedLines.map((ol) => {
            const received = enteredQuantities[ol.id_variant] || 0
            const remaining = Math.max(0, ol.quantity - received)
            return {
              id_variant: ol.id_variant,
              detail: ol.detail,
              quantity_ordered: ol.quantity,
              quantity_received: received,
              quantity_remaining: remaining,
              quantity_to_enter: remaining,
            }
          })

          setLines(inventoryLines)
          setLoadingOperations(false)
        }).catch(() => {
          const inventoryLines: InventoryLine[] = orderedLines.map((ol) => ({
            id_variant: ol.id_variant,
            detail: ol.detail,
            quantity_ordered: ol.quantity,
            quantity_received: 0,
            quantity_remaining: ol.quantity,
            quantity_to_enter: ol.quantity,
          }))
          setLines(inventoryLines)
          setLoadingOperations(false)
        })
      } else {
        const inventoryLines: InventoryLine[] = orderedLines.map((ol) => ({
          id_variant: ol.id_variant,
          detail: ol.detail,
          quantity_ordered: ol.quantity,
          quantity_received: 0,
          quantity_remaining: ol.quantity,
          quantity_to_enter: ol.quantity,
        }))
        setLines(inventoryLines)
      }
    } else {
      setLines([])
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerOrder])

  return (
    <div id="modal-provider-order-storage-center" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.storagePurchase}</h2>
          </div>
          <div className="modal-body">
            { companyForUser && storageCentersForCompany && providerOrder
              ?
              <div className="form">
                <div className="text-justify fw-bold mb-5">
                  {lang.labels.theSystemAddingFollowingProductsToCompanyInventoryProviderOrder}
                </div>
                <div className="fv-row mb-5">
                  <label className="form-label required">{lang.labels.storageCenter}</label>
                  <Dropdown>
                    <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                      <span className="selection">
                        <span className="select2-selection select2-selection--single form-select">
                          <span className="select2-selection__rendered" role="textbox">
                            {storageCenter.valid ? storageCenterSelected(storageCentersForCompany, storageCenter.value) : lang.labels.selectOption}
                          </span>
                        </span>
                      </span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                      <span className="select2-dropdown select2-dropdown--below">
                        <span className="select2-results">
                          <ul className="select2-results__options" role="listbox">
                            { storageCentersForCompany.map (( (item, index) => { return (
                              <li key={index} className={`select2-results__option select2-results__option--selectable ${item.id === storageCenter.value && "select2-results__option--selected"}`} role="option" aria-selected={item.id === storageCenter.value}>
                                <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeStorageCenter(item.id)}>{item.name}</Dropdown.Item>
                              </li>
                            )}))}
                          </ul>
                        </span>
                      </span>
                    </Dropdown.Menu>
                  </Dropdown>
                  <AppLegend component="modal-provider-order-storage-center" attribute={{check: storageCenter.valid, name: 'storage-center', index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
                </div>
                { loadingOperations
                  ?
                  <AppLoading></AppLoading>
                  :
                  allReceived
                  ?
                  <div className="text-center text-success fw-bold fs-6 py-5">
                    {lang.labels.allItemsReceived}
                  </div>
                  :
                  <div className="dataTables_wrapper">
                    <table className="table align-middle table-row-dashed gy-1 mb-2">
                      <thead className="border-bottom">
                        <tr className="text-muted text-uppercase fw-bolder fs-7">
                          <th className="min-w-150px text-nowrap text-start">{lang.labels.description}</th>
                          <th className="w-80px text-nowrap text-center">{lang.labels.quantityOrdered}</th>
                          <th className="w-80px text-nowrap text-center">{lang.labels.quantityReceived}</th>
                          <th className="w-80px text-nowrap text-center">{lang.labels.quantityRemaining}</th>
                          <th className="w-100px text-nowrap text-center">{lang.labels.quantityToEnter}</th>
                        </tr>
                      </thead>
                      <tbody className="text-muted fw-bold">
                        { lines.map (( (item, index) => { return (
                          <tr key={index} className={item.quantity_remaining <= 0 ? 'opacity-50' : ''}>
                            <td className="text-secondary text-start">{item.detail}</td>
                            <td className="text-center">{item.quantity_ordered}</td>
                            <td className="text-center">{item.quantity_received}</td>
                            <td className="text-center">{item.quantity_remaining}</td>
                            <td className="text-center">
                              { item.quantity_remaining > 0
                                ?
                                <input
                                  className="form-control text-center w-80px mx-auto"
                                  type="number"
                                  min={0}
                                  max={item.quantity_remaining}
                                  value={item.quantity_to_enter}
                                  onChange={(event) => handleChangeQuantity(index, event.target.value)}
                                />
                                :
                                <span className="badge badge-light-success">
                                  <i className="bi bi-check-lg text-success"></i>
                                </span>
                              }
                            </td>
                          </tr>
                        )}))}
                      </tbody>
                    </table>
                  </div>
                }
              </div>
              :
              <AppLoading></AppLoading>
            }
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalOrderStorageCenter}>{lang.labels.noCancel}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitProviderOrderValidate} disabled={allReceived || !hasLinesToEnter}>
              <span className="indicator-label">{lang.labels.yesAdd}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
};

export default AppModalProviderOrderStorageCenter;
